import { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useTranslation } from "react-i18next";
import { InfoCard, SnackBar } from "src/design";
import { CircularLoader } from "src/design/CircularLoader";
import { PaymentStatus } from "src/business/payment/payment";
import {
  AdminPaymentStatusFilter,
  useAdminPayments,
} from "src/data/admin/queries/payment";
import { AdminPaymentDto } from "src/data/admin/types/payment";
import { useGetSubscriptionTiers } from "src/data/landing/queries/subscriptionTiers";
import {
  colorAccent,
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";
import { AdminPaymentRow } from "./components/AdminPaymentRow";
import { ConfirmPaymentDialog } from "./components/ConfirmPaymentDialog";
import { ProofPreviewDialog } from "./components/ProofPreviewDialog";
import { RejectPaymentDialog } from "./components/RejectPaymentDialog";

const STATUS_OPTIONS: PaymentStatus[] = [
  "PENDING",
  "CONFIRMED",
  "REJECTED",
  "CANCELLED",
];

/** Sentinel for the unfiltered view — a Select cannot hold `null` as a value. */
const ALL = "all";

const headerCellSx = { color: colorTextMuted, fontWeight: 600 } as const;

type Snack = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

/**
 * The ops queue. It opens on PENDING because that is the work to be done, and
 * the filter is passed to the backend rather than applied here so each view
 * caches independently.
 */
const Payments = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AdminPaymentStatusFilter>("PENDING");
  const [previewPaymentId, setPreviewPaymentId] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<AdminPaymentDto | null>(
    null,
  );
  const [rejectTarget, setRejectTarget] = useState<AdminPaymentDto | null>(
    null,
  );
  const [snack, setSnack] = useState<Snack>({
    open: false,
    message: "",
    severity: "success",
  });

  const { data: payments, isLoading, isError, error, refetch } =
    useAdminPayments(status);
  const { data: catalog } = useGetSubscriptionTiers();

  const tiers = useMemo(
    () => (catalog ?? []).map((plan) => plan.name),
    [catalog],
  );

  const ordered = useMemo(
    () =>
      [...(payments ?? [])].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [payments],
  );

  // A decision on a payment an admin elsewhere already resolved comes back 409.
  // Refetching turns that into the row's real current status rather than a
  // stale queue offering the same refusal again.
  const reportFailure = (message: string) => {
    setSnack({ open: true, message, severity: "error" });
    void refetch();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: colorTextPrimary }}
          >
            {t("payments.admin.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: colorTextMuted }}>
            {t("payments.admin.subtitle")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("payments.admin.filter.label")}</InputLabel>
            <Select
              label={t("payments.admin.filter.label")}
              value={status ?? ALL}
              onChange={(event) =>
                setStatus(
                  event.target.value === ALL
                    ? null
                    : (event.target.value as PaymentStatus),
                )
              }
            >
              <MenuItem value={ALL}>{t("payments.admin.filter.all")}</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`payments.status.${option}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <InfoCard
        title={t("payments.admin.queueTitle")}
        subtitle={t("payments.admin.queueSubtitle")}
        icon={<PaymentsOutlinedIcon sx={{ color: colorAccent }} />}
      >
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularLoader />
          </Box>
        )}

        {!isLoading && isError && (
          <Typography variant="body2" sx={{ color: colorTextError, py: 2 }}>
            {error instanceof Error
              ? error.message
              : t("payments.admin.loadError")}
          </Typography>
        )}

        {!isLoading && !isError && ordered.length === 0 && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
              {t("payments.admin.empty.title")}
            </Typography>
            <Typography variant="body2" sx={{ color: colorTextMuted }}>
              {t("payments.admin.empty.message")}
            </Typography>
          </Box>
        )}

        {!isLoading && !isError && ordered.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, pl: 0 }}>
                    {t("payments.admin.columns.restaurant")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.type")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.tier")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.duration")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.amount")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.status")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("payments.admin.columns.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordered.map((payment) => (
                  <AdminPaymentRow
                    key={payment.id}
                    payment={payment}
                    onPreviewProof={setPreviewPaymentId}
                    onConfirm={setConfirmTarget}
                    onReject={setRejectTarget}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </InfoCard>

      <ProofPreviewDialog
        paymentId={previewPaymentId}
        onClose={() => setPreviewPaymentId(null)}
      />

      <ConfirmPaymentDialog
        payment={confirmTarget}
        tiers={tiers}
        onClose={() => setConfirmTarget(null)}
        onConfirmed={(payment) =>
          setSnack({
            open: true,
            message: t("payments.admin.confirm.success", {
              restaurant: payment.restaurantName,
            }),
            severity: "success",
          })
        }
        onError={reportFailure}
      />

      <RejectPaymentDialog
        payment={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onRejected={(payment) =>
          setSnack({
            open: true,
            message: t("payments.admin.reject.success", {
              restaurant: payment.restaurantName,
            }),
            severity: "success",
          })
        }
        onError={reportFailure}
      />

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default Payments;
